import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { ProductProfileList } from "src/app/features/ProductProfile/types/ProductProfileList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const ProductProfilesTable: FC<ResourceTableProps<ProductProfileList>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<ProductProfileList>): ExtendedColumnsType<ProductProfileList>[] => {
            return [
                {
                    title: t("productProfile.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("productProfile.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("productProfile.organizationUnits"),
                    key: "numberOfOrganizationUnits",
                    dataIndex: "numberOfOrganizationUnits",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "numberOfOrganizationUnits" ? sortInfo.order : null,
                },
                {
                    title: t("productProfile.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("productProfile.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: ProductProfileList) =>
        navigate(`${BASE_PATH}/${ResourceName.PRODUCT_PROFILES}/${FORM_ACTION.EDIT}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<ProductProfileList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="productProfiles_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: ProductProfileList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
